/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { ChangeEvent, FormEvent, useState, useEffect, useContext } from "react";
import axios from "axios";
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalBody,
} from "@chakra-ui/modal";
import { Select, Button } from '@chakra-ui/react';
import InputField from "components/fields/InputField";

interface RequestData {
    address: string;
    nickname: string;
    asset: string;
}

const RequestNewWhitelistAddressModal: React.FC<{
    setSwalProps: any;
    isOpen: boolean;
    onClose(): void;
    triggerReloadList(): void;
}> = ({ setSwalProps, isOpen, onClose, triggerReloadList }) => {
    const userRole = localStorage.getItem("userRole")?.toLowerCase();
    const [isCreating, setIsCreating] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<any>("");
    const subaccountId = localStorage.getItem("subaccountId");
    const [isFetchingAsset, setIsFetchingAsset] = useState(false);
    const [cryptoAssets, setCryptoAssets] = useState<any>([]);
    const [requestData, setRequestData] = useState<RequestData>({
        address: "",
        nickname: "",
        asset: ""
    });

    const fetchAssets = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/trade/assets`);
            const data = response.data.assets.payload;

            const tempCryptoAssets: any[] = [];

            // Iterate through each asset type
            Object.keys(data).forEach((assetCode) => {
                const methods = data[assetCode];

                // Check if methods array exists and has items
                if (methods && methods.length > 0) {
                    const firstMethod = methods[0];

                    // Only process crypto assets
                    if (firstMethod.crypto === true) {
                        // Crypto asset - add ALL asset-network combinations
                        if (firstMethod.networks && firstMethod.networks.length > 0) {
                            firstMethod.networks.forEach((network: any) => {
                                const uniqueId = `${assetCode}-${network.network}`;

                                tempCryptoAssets.push({
                                    id: uniqueId,
                                    code: assetCode,
                                    method_id: firstMethod.method_id,
                                    method_name: firstMethod.method_name,
                                    name: `${assetCode} (${network.display_name})`, // "USDT (TRC20)"
                                    crypto: true,
                                    decimal: firstMethod.decimal,
                                    min_trade: firstMethod.min_trade,
                                    network: network.network,
                                    network_pk: network.network_pk,
                                    display_name: network.display_name,
                                    chain: network.chain,
                                    is_default: network.is_default,
                                    asset_pk: firstMethod.asset_pk || assetCode,
                                });
                            });
                        }
                    }
                }
            });

            setCryptoAssets(tempCryptoAssets);
        } catch (error) {
            console.error("Error fetching data", error);
            setCryptoAssets([]);
        }
    };

    const closeModal = async () => {
        onClose();
        setErrorMessage("");
        setRequestData({
            address: "",
            nickname: "",
            asset: ""
        });
    };

    const submitHandler = async (e: any) => {
        setErrorMessage("");

        try {
            setIsCreating(true);
            
            // Find the selected crypto asset to get the network
            const selectedAsset = cryptoAssets.find((asset: any) => asset.id === requestData.asset);
            
            if (!selectedAsset) {
                setErrorMessage('Invalid asset selection');
                setIsCreating(false);
                return;
            }

            const postData = {
                suba_id: subaccountId,
                address: requestData.address,
                nickname: requestData.nickname,
                asset: selectedAsset.code,
                network: selectedAsset.network,
                bypass: false,
                username: localStorage.getItem("username"),
                userRole: userRole
            };

            await axios.post(
                process.env.REACT_APP_API_URL + `/api/${userRole}/trade/action/request-new-whitelist-address`,
                postData
            )
                .then(function (response) {
                    if (response && response.data?.status === 'ok') {
                        closeModal();
                        setSwalProps({
                            show: true,
                            icon: "success",
                            title: "Success",
                            html: "Request new whitelist address successfully",
                            showConfirmButton: true,
                            didClose: () => {
                                triggerReloadList();
                                setSwalProps({});
                                setIsCreating(false);
                            },
                        });
                    } else {
                        setIsCreating(false);
                        setErrorMessage('Request new whitelist address has been failed');
                    }
                })
                .catch(function (error) {
                    setIsCreating(false);
                    if (error.response) {
                        setErrorMessage(error.response.data.message);
                    } else {
                        setErrorMessage('Request new whitelist address has been failed');
                    }
                });
        } catch (error: any) {
            setIsCreating(false);
            setSwalProps({
                show: true,
                icon: "error",
                title: "Oops!",
                html: "Something went wrong, please try again later.",
                showConfirmButton: true,
                didClose: () => {
                    setSwalProps({});
                    setIsCreating(false);
                },
            });
        }
    };

    const handleInputChange = (
        e: ChangeEvent<HTMLInputElement | HTMLSelectElement>
    ) => {
        const { id, value } = e.target;
        setRequestData(prev => ({
            ...prev,
            [id]: value,
        }));
    };

    useEffect(() => {
        const fetchData = async () => {
            setIsFetchingAsset(true);
            await fetchAssets();
            setIsFetchingAsset(false);
        };

        if (isOpen) {
            fetchData();
        }
    }, [isOpen]); // eslint-disable-line

    const isFormValid = requestData.nickname !== "" && requestData.address !== "" && requestData.asset !== "";

    return (
        <>
            <Modal isOpen={isOpen} onClose={onClose} closeOnOverlayClick={false}>
                <ModalOverlay className="bg-[#000] !opacity-30" />
                <ModalContent className="dark:!bg-neutral-900 !z-[1002] !m-auto !w-full max-w-xl !rounded-2xl !border !border-gray-200 !bg-white !shadow-xl dark:!border-white/10 md:!top-[12vh]">
                    <ModalBody className="p-6 md:p-7">
                        <div className="mb-4 flex items-start gap-3">
                            <div className="rounded-xl bg-primary-50 p-2 text-primary-600 ring-1 ring-primary-100 dark:bg-primary-900/20 dark:text-primary-400 dark:ring-primary-900/30">
                                <svg
                                    viewBox="0 0 24 24"
                                    className="h-5 w-5"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                >
                                    <path d="M12 4v16m8-8H4" />
                                </svg>
                            </div>
                            <div>
                                <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                                    Request New Whitelist Address
                                </h1>
                                <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                                    Submit a request to add a new address to your whitelist
                                </p>
                            </div>
                        </div>

                        <form>
                            <div className="mb-6 space-y-4">
                                <div className="customForm">
                                    <InputField
                                        disabled={isCreating}
                                        variant="auth"
                                        extra="shadow-sm"
                                        label="Nickname"                                        
                                        required={true}
                                        placeholder="Enter nickname"
                                        id="nickname"
                                        type="text"
                                        autoComplete="off"
                                        value={requestData.nickname}
                                        onChange={handleInputChange}
                                    />
                                </div>

                                <div className="customForm">
                                    <InputField
                                        disabled={isCreating}
                                        variant="auth"
                                        extra="shadow-sm"
                                        label="Address"
                                        required={true}
                                        placeholder="Enter address"
                                        id="address"
                                        type="text"
                                        autoComplete="off"
                                        value={requestData.address}
                                        onChange={handleInputChange}
                                    />
                                </div>

                                <div>
                                    <label className="mb-1 block text-sm font-medium text-navy-700 dark:text-white">
                                        Asset <span className="text-red-500">*</span>
                                    </label>
                                    <Select
                                        id="asset"
                                        value={requestData.asset}
                                        onChange={handleInputChange}
                                        disabled={isCreating || isFetchingAsset}
                                        className="h-10 rounded-md custom-select relative bg-white border border-gray-300 focus:outline-none py-2 pl-3 pr-10 sm:text-sm w-full shadow-sm"
                                        required
                                    >
                                        <option value="">{isFetchingAsset ? "Retreiving..." : "-- Please Select --"}</option>
                                        {cryptoAssets.map((asset: any) => (
                                            <option key={asset.id} value={asset.id}>
                                                {asset.name}
                                            </option>
                                        ))}
                                    </Select>
                                </div>

                                {errorMessage && (
                                    <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-300">
                                        {errorMessage}
                                    </div>
                                )}
                            </div>

                            <div className="flex justify-end gap-2">
                                <Button
                                    onClick={() => closeModal()}
                                    isDisabled={isCreating}
                                    className="btn btn-default"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    isDisabled={!isFormValid || isCreating}
                                    onClick={submitHandler}
                                    isLoading={isCreating}
                                    loadingText='Requesting...'
                                    className="btn btn-primary">
                                    Request
                                </Button>
                            </div>
                        </form>
                    </ModalBody>
                </ModalContent>
            </Modal>
        </>
    );
};

export default RequestNewWhitelistAddressModal;